import { createApi } from '@reduxjs/toolkit/query/react';
import dayjs from 'dayjs';

import { setUser } from 'store/user/userSlice';

import { logDataDogEvent } from 'utils/datadog';
import { DateFormat } from 'utils/date';
import { baseQueryWithReauth } from 'utils/services';

import {
  AccountBalance,
  AddProfileImageReqProps,
  AddProfileImageResProps,
  GetBillingHistoryQueryResProps,
  GetBillingHistoryReqProps,
  GetBillingHistoryResProps,
  GetCreditBalanceReqProps,
  GetCreditBalanceResProps,
  GetCreditCardInformationReqProps,
  GetCreditCardInformationResProps,
  GetInvoiceReqProps,
  GetInvoiceResProps,
  GetMyAccountReqProps,
  GetMyAccountResProps,
  GetPlanRecommendationResProps,
  GetSubscriptionInfoResProps,
  GetSubscriptionsReqProps,
  GetSubscriptionsResProps,
  MakePatientAsyncReqProps,
  MarkInitialLoginReqProps,
  MarkInitialLoginResProps,
  PayInvoiceReqProps,
  PayInvoiceResProps,
  PreviewChangeProgramReqProps,
  PreviewChangeProgramResProps,
  SendRecommendationDecisionReqProps,
  UpdateMyAccountReqProps,
  UpdateMyAccountResProps,
  UpdatePaymentProfileReqProps,
  UpdatePaymentProfileResProps
} from './myAccount.types';

const getBillingHistoryStatus = (
  status: string,
  acceptanceStatus?: { value: string } | null
): string => {
  switch (acceptanceStatus?.value) {
    case 'pending_acceptance':
      return 'Pending acceptance';
    case 'canceled':
      return 'Canceled';
    case 'expired':
      return 'Expired';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

export const myAccountApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    addProfileImage: build.mutation<AddProfileImageResProps, AddProfileImageReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/my-account/profile-image'
      })
    }),
    deletePaymentMethod: build.mutation<
      UpdateMyAccountResProps,
      { id: string; isInsuranceSubscription?: boolean }
    >({
      query: ({ id, isInsuranceSubscription }) => ({
        method: 'DELETE',
        ...(isInsuranceSubscription && { params: { isInsuranceSubscription: true } }),
        url: `/my-account/payment-profile/${id}`
      })
    }),
    getBillingHistory: build.query<GetBillingHistoryResProps, GetBillingHistoryReqProps>({
      keepUnusedDataFor: 0,
      query: (params) => ({
        params,
        url: '/my-account/billing-history'
      }),
      transformResponse: (response: GetBillingHistoryQueryResProps) => {
        return {
          ...response,
          data: response.data?.map((el) => ({
            amount: +el.amount,
            cardBrand:
              el.payment?.paymentMethod.cardBrand ||
              (el.payment?.paymentMethod.email ? 'PayPal' : ''),
            credit: +(el.credit?.appliedAmount || '0'),
            date: dayjs(el.createdAt).format(DateFormat.MMMM_DD_YYYY),
            discountAmount: +(el.discount?.discountAmount || '0'),
            discountTitle: el.discount?.title || '',
            email: el.payment?.paymentMethod.email || '',
            invoice: el.title,
            invoiceUrl: el.invoiceUrl,
            maskedCardNumber: el.payment?.paymentMethod.maskedCardNumber || '',
            number: el.number,
            paidAmount: +el.paidAmount,
            refundAmount: +el.refundAmount,
            status: getBillingHistoryStatus(el.status, el.acceptanceStatus),
            subInvoice: el.subTitle
          }))
        };
      }
    }),
    getCreditBalance: build.query<AccountBalance[], GetCreditBalanceReqProps>({
      keepUnusedDataFor: 0,
      query: () => '/my-account/account-balances',
      transformResponse: (response: GetCreditBalanceResProps) => response.data
    }),
    getCreditCardInformation: build.query<
      GetCreditCardInformationResProps,
      GetCreditCardInformationReqProps
    >({
      query: (params) => ({
        ...(params && { params }),
        url: '/my-account/credit-card-information'
      })
    }),
    getInvoice: build.query<GetInvoiceResProps, GetInvoiceReqProps>({
      keepUnusedDataFor: 0,
      query: (invoiceId) => ({
        url: `/my-account/invoices/${invoiceId}`
      })
    }),
    getMyAccount: build.query<GetMyAccountResProps, GetMyAccountReqProps>({
      keepUnusedDataFor: 0,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(setUser(data.data));
          }
        } catch (e) {
          logDataDogEvent({
            context: { error: e },
            level: 'error',
            message: 'getMyAccount sync failed'
          });
        }
      },
      query: () => '/my-account'
    }),
    getPlanRecommendation: build.query<GetPlanRecommendationResProps, void>({
      query: () => '/my-account/plan-recommendation'
    }),
    getSubscriptionInfo: build.query<GetSubscriptionInfoResProps, void>({
      query: () => '/my-account/subscription-info'
    }),
    getSubscriptions: build.query<GetSubscriptionsResProps, GetSubscriptionsReqProps>({
      query: (params) => ({
        ...(params && { params }),
        url: '/my-subscriptions'
      })
    }),
    makePatientAsync: build.mutation<unknown, MakePatientAsyncReqProps>({
      query: (body) => ({
        body,
        method: 'PATCH',
        url: '/my-account/make-async'
      })
    }),
    markInitialLogin: build.mutation<MarkInitialLoginResProps, MarkInitialLoginReqProps>({
      query: () => ({
        method: 'PATCH',
        url: '/my-account/mark-initial-login'
      })
    }),
    payInvoice: build.mutation<PayInvoiceResProps, PayInvoiceReqProps>({
      query: (invoiceId) => ({
        method: 'POST',
        url: `/my-account/invoices/${invoiceId}/pay`
      })
    }),
    previewChangeProgram: build.mutation<
      PreviewChangeProgramResProps,
      PreviewChangeProgramReqProps
    >({
      query: (body) => ({
        body,
        method: 'PATCH',
        url: '/preview-change-program'
      })
    }),
    sendRecommendationDecision: build.mutation<unknown, SendRecommendationDecisionReqProps>({
      query: (body) => ({
        body,
        method: 'PATCH',
        url: '/my-account/plan-recommendation'
      })
    }),
    updateDefaultPaymentMethod: build.mutation<
      UpdateMyAccountResProps,
      { id: string; isInsuranceSubscription?: boolean }
    >({
      query: ({ id, isInsuranceSubscription }) => ({
        method: 'PUT',
        ...(isInsuranceSubscription && { params: { isInsuranceSubscription: true } }),
        url: `/my-account/payment-profile/${id}/set-default`
      })
    }),
    updateMyAccount: build.mutation<UpdateMyAccountResProps, UpdateMyAccountReqProps>({
      query: (body) => ({
        body,
        method: 'PATCH',
        url: '/my-account'
      })
    }),
    updatePaymentProfile: build.mutation<
      UpdatePaymentProfileResProps,
      UpdatePaymentProfileReqProps
    >({
      query: ({ isInsuranceSubscription, method, ...body }) => ({
        body,
        method,
        ...(isInsuranceSubscription && { params: { isInsuranceSubscription: true } }),
        url: '/my-account/payment-profile'
      })
    })
  }),
  reducerPath: 'myAccountApi',
  tagTypes: ['MyAccount']
});

export const {
  useAddProfileImageMutation,
  useDeletePaymentMethodMutation,
  useGetBillingHistoryQuery,
  useGetCreditBalanceQuery,
  useGetCreditCardInformationQuery,
  useGetInvoiceQuery,
  useGetMyAccountQuery,
  useGetPlanRecommendationQuery,
  useGetSubscriptionInfoQuery,
  useLazyGetCreditCardInformationQuery,
  useLazyGetMyAccountQuery,
  useLazyGetSubscriptionInfoQuery,
  useMakePatientAsyncMutation,
  useMarkInitialLoginMutation,
  usePayInvoiceMutation,
  usePreviewChangeProgramMutation,
  useSendRecommendationDecisionMutation,
  useUpdateDefaultPaymentMethodMutation,
  useUpdateMyAccountMutation,
  useUpdatePaymentProfileMutation
} = myAccountApi;
